import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import { products } from '../services/products';
import { useI18n } from '../context/I18nContext';

const ProductListingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, translateCategory } = useI18n();

  const [selectedCategory, setSelectedCategory] = useState('All');
  const [sortBy, setSortBy] = useState('default');
  const [maxPrice, setMaxPrice] = useState(300);
  const [loading, setLoading] = useState(true);

  // Parse URL params for category filter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const cat = params.get('category');
    if (cat) setSelectedCategory(cat);
    // Simulate loading
    setTimeout(() => setLoading(false), 500);
  }, [location.search]);

  const categories = ['All', 'Dog', 'Cat', 'Wild Animals'];

  const filtered = useMemo(() => {
    let result = [...products];
    if (selectedCategory !== 'All') {
      result = result.filter(p => p.category === selectedCategory);
    }
    result = result.filter(p => p.price <= maxPrice);
    if (sortBy === 'price-asc') result.sort((a, b) => a.price - b.price);
    else if (sortBy === 'price-desc') result.sort((a, b) => b.price - a.price);
    else if (sortBy === 'rating') result.sort((a, b) => b.rating - a.rating);
    return result;
  }, [selectedCategory, sortBy, maxPrice]);

  const handleCategoryChange = (cat) => {
    setSelectedCategory(cat);
    if (cat !== 'All') {
      navigate(`/products?category=${encodeURIComponent(cat)}`);
    } else {
      navigate('/products');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl animate-bounce mb-4">🐾</div>
          <p className="text-amber-700 font-medium">{t('listing.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-amber-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-800 to-amber-700 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl md:text-4xl font-bold font-serif mb-2">{t('listing.title')}</h1>
          <p className="text-amber-200">{t('listing.discover', { count: filtered.length })}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters and Sorting */}
        <div className="bg-white rounded-2xl p-6 mb-8 shadow-sm border border-amber-100">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Category Filter */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-amber-900 mb-3">{t('listing.category')}</label>
              <div className="flex flex-wrap gap-2">
                {categories.map(cat => (
                  <button
                    key={cat}
                    onClick={() => handleCategoryChange(cat)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                      selectedCategory === cat
                        ? 'bg-amber-700 text-white'
                        : 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200'
                    }`}
                  >
                    {translateCategory(cat)}
                  </button>
                ))}
              </div>
            </div>

            {/* Price Range */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-amber-900 mb-3">
                {t('listing.maxPrice')}: <span className="text-amber-700 font-bold">${maxPrice}</span>
              </label>
              <input
                type="range"
                min="50"
                max="300"
                step="10"
                value={maxPrice}
                onChange={(e) => setMaxPrice(Number(e.target.value))}
                className="w-full accent-amber-700"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>$50</span><span>$300</span>
              </div>
            </div>

            {/* Sort */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-amber-900 mb-3">{t('listing.sortBy')}</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full border border-amber-200 rounded-lg px-4 py-2 text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-amber-500"
              >
                <option value="default">{t('listing.sort.featured')}</option>
                <option value="price-asc">{t('listing.sort.priceAsc')}</option>
                <option value="price-desc">{t('listing.sort.priceDesc')}</option>
                <option value="rating">{t('listing.sort.rating')}</option>
              </select>
            </div>
          </div>
        </div>

        {/* Products Grid */}
        {filtered.length === 0 ? (
          <div className="text-center py-20">
            <span className="text-6xl mb-4 block">😔</span>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">{t('listing.noProducts')}</h3>
            <p className="text-gray-500">{t('listing.adjustFilters')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filtered.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductListingPage;
